import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box, Drawer, List, Typography, useMediaQuery, Divider
} from '@mui/material';
import useAuth from 'src/context/AuthContext';
import { getAppRoutes } from 'src/utils/routeConstants';
import { USER_ROLES } from 'src/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { getApprovalsCount } from 'src/redux/cardApprovalSlice';
import { isAdminUser } from 'src/utils/getInitials';
import NavItem from './NavItem';
import { SIDE_NAV_WIDTH } from './DashboardNavbar';

const DashboardSidebar = ({
  onMobileClose,
  openMobile,
  isCollapsed = false,
  width = SIDE_NAV_WIDTH,
  onToggleCollapse
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const user = getUser();
  const { appSettings } = useSelector((state) => state.settings);
  const isAdmin = isAdminUser(getUser);
  const isApprover = user?.role === USER_ROLES.approver;
  const lgUp = useMediaQuery((th) => th.breakpoints.up('lg'));

  const appRoutes = getAppRoutes(user, appSettings);

  useEffect(() => {
    if ((isAdmin || isApprover) && !!location.state?.fromDashboard) {
      dispatch(getApprovalsCount());
    }
    const timeout = setTimeout(() => {
      if (openMobile && onMobileClose) {
        onMobileClose();
      }
    }, 150);
    return () => clearTimeout(timeout);
  }, [location.pathname]);

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#1F2937', // Changed from #111827 to softer Midnight Slate
        color: '#ffffff',
        overflow: 'hidden',
        borderRight: '1px solid rgba(255,255,255,0.05)'
      }}
    >
      {/* Brand Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          px: isCollapsed ? 1 : 3,
          minHeight: 80,
          transition: 'all 0.3s ease',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0) 100%)'
        }}
      >
        <Box
          component="img"
          src="/static/images/logo.png"
          alt="Logo"
          sx={{
            height: isCollapsed ? 32 : 36,
            width: 'auto',
            transition: 'all 0.3s ease',
            filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.4))' // Green glow on logo
          }}
        />
        {!isCollapsed && (
          <Box sx={{ ml: 2, transition: 'opacity 0.3s ease' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.5, lineHeight: 1 }}>
              RETRACK
            </Typography>
            <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 600, letterSpacing: 2 }}>
              AUTOMATION
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2, mb: 2 }} />

      {/* Navigation */}
      <Box
        sx={{
          flexGrow: 1,
          px: 2,
          pb: 2,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 4
          },
          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,0.3)' }
        }}
      >
        <List disablePadding>
          {appRoutes.map((item) => (
            <NavItem
              key={item.title}
              item={item}
              collapsed={isCollapsed}
              onToggleCollapse={onToggleCollapse}
            />
          ))}
        </List>
      </Box>

      {/* Footer / Version removed */}
    </Box>
  );

  const sidebarStyle = {
    border: 'none',
    width,
    bgcolor: '#1F2937', // Matching background
    color: '#fff',
    transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    overflowX: 'hidden',
    boxShadow: '4px 0 24px 0 rgba(0,0,0,0.1)',
  };

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        variant="permanent"
        PaperProps={{ sx: sidebarStyle }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onMobileClose}
      open={openMobile}
      variant="temporary"
      PaperProps={{ sx: sidebarStyle }}
    >
      {content}
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  width: PropTypes.number,
  onToggleCollapse: PropTypes.func
};

DashboardSidebar.defaultProps = {
  onMobileClose: () => { },
  openMobile: false
};

export default DashboardSidebar;
